import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MentionInput, MentionInputHandle, Mention } from './MentionInput';
import { MentionMenu } from './MentionMenu';
import {
  SlashCommandMenu,
  SLASH_COMMANDS,
  type SlashCommand,
} from './slash-commands';
import { ProjectPlaybook } from '../../services/Project';

interface HomeComposerProps {
  /** Playbooks that can be @-mentioned (mapped to the mention-menu shape). */
  playbooks: ProjectPlaybook[];
  placeholder: string;
  disabled?: boolean;
  sendAriaLabel?: string;
  /** Send a normal message. Text carries [[playbook:id]] tokens from mentions. */
  onSubmit: (text: string) => void;
  /** A slash command was chosen from the home composer (e.g. /review, /generate). */
  onSlashCommand: (command: SlashCommand) => void;
}

export interface HomeComposerHandle {
  setText: (value: string) => void;
  focus: () => void;
}

// From home a slash command means "start this", so the clear/export actions
// (which only apply inside an existing chat) are not offered here.
const HOME_COMMANDS = SLASH_COMMANDS.filter(
  (c) => c.id !== 'clear' && c.id !== 'export',
);

export const HomeComposer = forwardRef<HomeComposerHandle, HomeComposerProps>(
  (
    { playbooks, placeholder, disabled, sendAriaLabel, onSubmit, onSlashCommand },
    ref,
  ) => {
  const inputRef = useRef<MentionInputHandle>(null);

  useImperativeHandle(ref, () => ({
    setText: (value: string) => inputRef.current?.setText(value),
    focus: () => inputRef.current?.focus(),
  }));
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [isSlashOpen, setIsSlashOpen] = useState(false);
  const [slashFilter, setSlashFilter] = useState('');

  const hasText = text.trim().length > 0;

  const reset = useCallback(() => {
    inputRef.current?.clear();
    setText('');
    setMentions([]);
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    reset();
  }, [text, disabled, onSubmit, reset]);

  const handleSelectPlaybook = useCallback(
    (playbookId: string) => {
      const pb = playbooks.find((p) => p.playbookId === playbookId);
      if (!pb) return;
      inputRef.current?.insertMention({
        type: 'playbook',
        id: pb.playbookId,
        name: pb.playbookName,
      });
      setIsMentionOpen(false);
    },
    [playbooks],
  );

  const handleSlashSelect = useCallback(
    (command: SlashCommand) => {
      setIsSlashOpen(false);
      setSlashFilter('');
      reset();
      onSlashCommand(command);
    },
    [onSlashCommand, reset],
  );

  const mentionMenuPosition = useMemo(() => ({ top: 10, left: 10 }), []);

  return (
    <div
      data-tour="home-input"
      className="relative flex gap-3 items-end bg-background rounded-2xl border border-border shadow-sm p-2"
    >
      <div className="flex-1">
        <MentionInput
          ref={inputRef}
          mentions={mentions}
          onMentionsChange={setMentions}
          onTextChange={setText}
          onAtTrigger={() => setIsMentionOpen(true)}
          onAtRemoved={() => setIsMentionOpen(false)}
          onSubmit={handleSubmit}
          suppressSubmit={isMentionOpen || isSlashOpen}
          onSlashTrigger={(filter) => {
            setSlashFilter(filter);
            setIsSlashOpen(true);
          }}
          onSlashRemoved={() => {
            setIsSlashOpen(false);
            setSlashFilter('');
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="px-2 py-2 text-[15px] md:text-base"
        />
      </div>
      <Button
        size="icon"
        variant="outline"
        onClick={handleSubmit}
        disabled={!hasText || disabled}
        aria-label={sendAriaLabel}
        className="h-10 w-10 shrink-0 rounded-xl bg-muted/50 border-brand text-brand hover:bg-brand-light hover:text-brand disabled:bg-muted disabled:border-muted disabled:text-muted-foreground"
      >
        <Send className="h-4 w-4" />
      </Button>

      {/* Slash command menu, glued on top of the input. From home a command
          means "start this skill / draft", so project/file gating is relaxed. */}
      <SlashCommandMenu
        isOpen={isSlashOpen}
        onClose={() => {
          setIsSlashOpen(false);
          setSlashFilter('');
        }}
        commands={HOME_COMMANDS}
        filter={slashFilter}
        onSelect={handleSlashSelect}
        hasProject={false}
        hasFiles={false}
      />

      {/* Mention menu for @ playbook references. */}
      <MentionMenu
        isOpen={isMentionOpen}
        onClose={() => setIsMentionOpen(false)}
        position={mentionMenuPosition}
        files={[]}
        playbooks={playbooks}
        onSelectFile={() => {}}
        onSelectPlaybook={handleSelectPlaybook}
      />
    </div>
  );
  },
);

HomeComposer.displayName = 'HomeComposer';
